import { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import DashboardLayout from "../../Layout/DashboardLayout";
import { employeeID, companyID } from "../../Common/UIComponents";
import {
  fetchLocations,
  fetchLabsByLocation,
  createUpdateLabBooking,
  fetchGanttChartByLocation,
} from "../../API_Service/LabBooking_Slice";
import { useGlobalLoader } from "../../Common/GlobalLoader";
import LabBookingStep1Location from "./LabBookingLocation";
import LabBookingStep2Lab from "./LabBookingLab";
import LabBookingStep3Schedule from "./LabBookingSchedule";
import LabBookingGanttChart from "./LabBookingGantt";
import LabAvailabilityPopup from "./LabAvailabilityPopup";
import FacilityBookingScheduler from "./FacilityBookingScheduler";
import { getEmployeeID, getCompanyID } from "../../Common/UIComponents";
import { useNavigate } from "react-router-dom";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const toArray = (res) => {
  const d = res?.payload?.data ?? res?.payload ?? res?.data ?? res;
  return Array.isArray(d) ? d : [];
};

// ─────────────────────────────────────────────
// SECTION HEADER
// ─────────────────────────────────────────────

const SectionHeader = ({ step, icon, title, isDone, selectedLabel }) => (
  <div className="flex items-center justify-between mb-4">
    <h3 className="text-base font-bold flex items-center gap-2">
      <span
        className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0
        ${isDone ? "bg-[#436900] text-white" : "bg-[#08757c] text-white"}`}
      >
        {isDone ? (
          <span className="material-symbols-outlined text-[14px]">check</span>
        ) : (
          step
        )}
      </span>
      <span className="material-symbols-outlined text-[#08757c] text-[20px]">
        {icon}
      </span>
      {title}
    </h3>
    {isDone && selectedLabel && (
      <span className="text-xs font-bold text-[#436900] bg-[#bbf46e]/20 px-3 py-1 rounded-full flex items-center gap-1">
        <span className="material-symbols-outlined text-[14px]">
          check_circle
        </span>
        {selectedLabel}
      </span>
    )}
  </div>
);

// ─────────────────────────────────────────────
// BOOKING SUMMARY PANEL
// ─────────────────────────────────────────────

const BookingSummary = ({ selectedLocation, selectedLab, form }) => {
  const summaryItems = [
    {
      icon: "location_on",
      label: "Location",
      value: selectedLocation?.cityName ?? "—",
      active: !!selectedLocation,
    },
    {
      icon: "biotech",
      label: "Lab",
      value: selectedLab?.facilityName ?? "—",
      active: !!selectedLab,
    },
    {
      icon: "schedule",
      label: "Planned Start",
      value: form.Planned_Start_Date_Hrs
        ? new Date(form.Planned_Start_Date_Hrs).toLocaleString()
        : "—",
      active: !!form.Planned_Start_Date_Hrs,
    },
    {
      icon: "timer",
      label: "Planned Hours",
      value: form.Planned_Hrs ? `${form.Planned_Hrs} hrs` : "—",
      active: !!form.Planned_Hrs,
    },
  ];

  const progress = Math.round(
    (summaryItems.filter((i) => i.active).length / summaryItems.length) * 100,
  );

  return (
    <div className=" bg-white rounded-3xl shadow-[0px_20px_40px_rgba(24,28,29,0.08)] overflow-hidden border border-white/40">
      <div className="bg-gradient-to-br from-[#08757c] to-[#005b60] p-4 text-white">
        <h3 className="text-base font-extrabold">Booking Summary</h3>
        <p className="text-white/60 text-[10px] uppercase font-bold tracking-widest mt-1">
          Review your selection
        </p>
        <div className="mt-3">
          <div className="flex justify-between text-[10px] font-bold text-white/60 mb-1">
            <span>Completion</span>
            <span>{progress}%</span>
          </div>
          <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#bbf46e] rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
      <div className="p-4 space-y-3">
        {summaryItems.map((item) => (
          <div key={item.label} className="flex items-start gap-3">
            <div
              className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0
              ${item.active ? "bg-[#e8f4f5] text-[#08757c]" : "bg-[#f1f4f4] text-[#bdc9c9]"}`}
            >
              <span className="material-symbols-outlined text-[18px]">
                {item.icon}
              </span>
            </div>
            <div>
              <p className="text-[10px] font-bold text-[#3e494a]/60 uppercase tracking-widest">
                {item.label}
              </p>
              <p
                className={`text-sm font-bold mt-0.5 ${item.active ? "text-[#181c1d]" : "text-[#bdc9c9]"}`}
              >
                {item.value}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────

const EMPTY_FORM = {
  BookingId: 0,
  Project_ID: 0,
  Project_Test_Det_ID: 0,
  Booking_Remarks: "",
  Planned_Hrs: "",
  Actual_Hrs: "",
  Planned_Start_Date_Hrs: "",
  Planned_End_Date_Hrs: "",
  Actual_Start_Date_Hrs: "",
  Actual_End_Date_Hrs: "",
};

const GanttSideCard = ({ companyID }) => {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!expanded) return;

    window.history.pushState({ ganttModal: true }, "");

    const handlePopState = (event) => {
      setExpanded(false);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [expanded]);
  const closeModal = () => {
    setExpanded(false);
    // If we pushed a history entry for this modal, clean it up
    if (window.history.state?.ganttModal) {
      window.history.back();
    }
  };

  const dispatch = useDispatch();
  const [data, setData] = useState({
    mainList: [],
    parentList: [],
    projectList: [],
    subList: [],
  });
  const [loading, setLoading] = useState(false);

  const todayStr = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(todayStr);
  const [toDate, setToDate] = useState(todayStr);

  useEffect(() => {
    if (!companyID) return;
    setLoading(true);
    dispatch(
      fetchGanttChartByLocation({
        startDateTime: fromDate,
        endDateTime: toDate,
        companyID,
      }),
    )
      .unwrap()
      .then((res) => {
        const d = res?.data ?? res;
        setData(d);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [companyID, dispatch]);

  return (
    <>
      {/* ── Side card (collapsed preview) ── */}
      <div className="bg-white rounded-3xl shadow-[0px_20px_40px_rgba(24,28,29,0.08)] overflow-hidden border border-white/40">
        <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-br from-[#08757c] to-[#005b60]">
          <h3 className="text-white font-extrabold text-sm flex items-center gap-2">
            <span className="material-symbols-outlined text-[18px]">
              bar_chart
            </span>
            Booking Gantt Chart
          </h3>
          <button
            onClick={() => setExpanded(true)}
            className="flex items-center gap-1 text-white/80 hover:text-white text-xs font-semibold bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full transition-all"
          >
            <span className="material-symbols-outlined text-[16px]">
              open_in_full
            </span>
            Expand
          </button>
        </div>
        <div className="px-5 py-4">
          {loading ? (
            <p className="text-xs text-[#64748b] text-center py-4">
              Loading chart…
            </p>
          ) : (
            <div className="space-y-2">
              {(data?.mainList ?? [])
                .filter((loc) => loc.StartDate !== "NA")
                .slice(0, 3)
                .map((loc) => (
                  <div
                    key={loc.LocationId}
                    className="flex items-center justify-between text-xs"
                  >
                    <span className="flex items-center gap-1.5 text-[#3e494a] font-medium">
                      <span className="w-2 h-2 rounded-full bg-[#1a56a0] inline-block" />
                      {loc.Title}
                    </span>
                    <span className="text-[#08757c] font-bold">
                      {Math.round((loc.LocationCompletedPercentage || 0) * 100)}
                      %
                    </span>
                  </div>
                ))}
              {(data?.mainList ?? []).filter((loc) => loc.StartDate !== "NA")
                .length === 0 &&
                !loading && (
                  <p className="text-xs text-[#94a3b8] text-center py-2">
                    No data available
                  </p>
                )}
              <p className="text-[10px] text-[#94a3b8] text-center pt-1">
                Click Expand to view full chart
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Full-page modal via portal ── */}
      {expanded &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 99999,
              background: "rgba(0,0,0,0.6)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                margin: "20px",
                flex: 1,
                background: "#fff",
                borderRadius: 16,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 24px 64px rgba(0,0,0,0.4)",
                maxHeight: "calc(100vh - 40px)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "14px 20px",
                  background: "linear-gradient(135deg, #08757c, #005b60)",
                  flexShrink: 0,
                }}
              >
                <h3
                  style={{
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 15,
                    margin: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: 20 }}
                  >
                    bar_chart
                  </span>
                  Booking Gantt Chart
                </h3>
                <button
                  onClick={() => setExpanded(false)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    color: "rgba(255,255,255,0.9)",
                    fontSize: 12,
                    fontWeight: 600,
                    background: "rgba(255,255,255,0.15)",
                    border: "none",
                    padding: "6px 14px",
                    borderRadius: 99,
                    cursor: "pointer",
                  }}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: 16 }}
                  >
                    close_fullscreen
                  </span>
                  Minimize
                </button>
              </div>
              <div style={{ flex: 1, overflow: "auto", padding: 16 }}>
                <LabBookingGanttChart
                  companyID={companyID}
                  data={data}
                  loading={loading}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

// ─────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────

export default function LabBookingIndex() {
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useGlobalLoader();

  const [locations, setLocations] = useState([]);
  const [labs, setLabs] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedLab, setSelectedLab] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showAvailability, setShowAvailability] = useState(false);
  const [showLabGrid, setShowLabGrid] = useState(false);
  const [prefillFacilityId, setPrefillFacilityId] = useState(null);

  const [scheduleSummary, setScheduleSummary] = useState({
    Planned_Start_Date_Hrs: null,
    Planned_Hrs: null,
  });

  // ── Ref for auto-scroll to labs section ──────────────────────────────────
  const labsSectionRef = useRef(null);
  const scheduleSectionRef = useRef(null);

  // ── Right-side panel (Booking Summary / Gantt) pinned via measured
  // position:fixed instead of CSS `sticky` — sidestepping any ancestor
  // overflow/transform quirks that were breaking sticky positioning. Only
  // applied at the lg breakpoint; below that the panel flows normally.
  const rightColRef = useRef(null);
  const [pinnedRect, setPinnedRect] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const updatePinnedRect = () => {
      if (rightColRef.current && window.innerWidth >= 1024) {
        const r = rightColRef.current.getBoundingClientRect();
        setPinnedRect({ left: r.left, width: r.width });
      } else {
        setPinnedRect(null);
      }
    };
    updatePinnedRect();
    window.addEventListener("resize", updatePinnedRect);
    return () => window.removeEventListener("resize", updatePinnedRect);
  }, []);

  // ── Load locations on mount ───────────────────────────────────────────────
  useEffect(() => {
    showLoader();
    dispatch(fetchLocations({ companyID: Number(getCompanyID()) || 0 }))
      .then((res) => setLocations(toArray(res)))
      .catch(() => {})
      .finally(() => hideLoader());
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load labs when location changes ──────────────────────────────────────
  useEffect(() => {
    if (!selectedLocation) return;
    showLoader();
    setLabs([]);
    setSelectedLab(null);
    dispatch(
      fetchLabsByLocation({
        ID: 0,
        locationID: selectedLocation.cityID,
        companyID,
      }),
    )
      .then((res) => setLabs(toArray(res)))
      .catch(() => {})
      .finally(() => hideLoader());
  }, [selectedLocation, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-scroll to labs section when location selected ───────────────────
  useEffect(() => {
    if (!selectedLocation || !labsSectionRef.current) return;
    setTimeout(() => {
      labsSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 300);
  }, [selectedLocation]);

  // ── Auto-scroll to schedule section when lab selected ────────────────────
  useEffect(() => {
    if (!selectedLab || !scheduleSectionRef.current) return;
    setTimeout(() => {
      scheduleSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 300);
  }, [selectedLab]);

  const handleFormChange = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
    setSaveSuccess(false);
    setSaveError(null);
  }, []);

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = (bookingData = {}) => {
    const errs = {};
    if (!selectedLocation) errs.location = "Please select a location.";
    if (!selectedLab) errs.lab = "Please select a lab.";
    if (!bookingData.Planned_Start_Date_Hrs && !form.Planned_Start_Date_Hrs)
      errs.start = "Planned Start is required.";
    if (!bookingData.Planned_End_Date_Hrs && !form.Planned_End_Date_Hrs)
      errs.end = "Planned End is required.";
    if (!bookingData.Planned_Hrs && !form.Planned_Hrs)
      errs.hrs = "Planned Hours is required.";
    return errs;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleConfirm = useCallback(
    async (bookingData) => {
      const errs = validate(bookingData);
      if (Object.keys(errs).length) {
        setErrors(errs);
        return;
      }
      debugger;
      const payload = {
        ...form,
        ...bookingData,
        BookingId: 0,
        FacilityId: selectedLab.facilityId,
        Actual_Hrs: Number(form.Actual_Hrs) || 0,
        TO_EmployeeID: getEmployeeID(),
        Created_By: getEmployeeID(),
        Modified_By: getEmployeeID(),
        CompanyID: getCompanyID(),
      };

      setIsSaving(true);
      showLoader();
      setSaveError(null);
      setSaveSuccess(false);

      dispatch(createUpdateLabBooking(payload))
        .then((res) => {
          const result = res?.payload;
          if (result?.success) {
            setSaveSuccess(true);
            setForm(EMPTY_FORM);
            setScheduleSummary({
              Planned_Start_Date_Hrs: null,
              Planned_Hrs: null,
            });
            setSelectedLocation(null);
            setSelectedLab(null);
            setLabs([]);
            window.scrollTo({ top: 0, behavior: "smooth" });
            setTimeout(() => setSaveSuccess(false), 5000);
          } else {
            setSaveError(
              result?.message ?? "Booking failed. Please try again.",
            );
          }
        })
        .catch((err) => setSaveError(err?.message ?? "Booking failed."))
        .finally(() => {
          setIsSaving(false);
          hideLoader();
        });
    },
    [form, selectedLab, selectedLocation, dispatch],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DashboardLayout>
      <div className="px-8 py-5 bg-[#f7fafa] min-h-screen text-[#181c1d]">
        {/* ── Breadcrumb ── */}
        <nav className="flex items-center gap-2 text-xs text-[#3e494a] mb-2">
          <span>Lab Management</span>
          <span className="material-symbols-outlined text-[14px]">
            chevron_right
          </span>
          <span className="text-[#08757C] font-semibold">Lab Booking</span>
        </nav>

        {/* ── Heading ── */}
        <div className="mb-5">
          <h1 className="text-2xl font-extrabold tracking-tight text-[#181c1d]">
            Lab Booking
          </h1>
          <p className="text-[#3e494a] text-sm mt-1">
            Reserve a lab facility by selecting your location, lab, and
            schedule.
          </p>
        </div>

        {/* ── Success banner ── */}
        {saveSuccess && (
          <div className="mb-6 bg-[#bbf46e]/20 text-[#436900] rounded-xl px-5 py-4 text-sm flex items-center gap-3 border border-[#bbf46e]/40">
            <span className="material-symbols-outlined text-[20px]">
              check_circle
            </span>
            <span className="font-semibold">
              Lab booking confirmed successfully!
            </span>
          </div>
        )}

        <div className="grid grid-cols-12 gap-6">
          {/* ── LEFT: 3 progressive step sections ── */}
          <div className="col-span-12 lg:col-span-8 space-y-4">
            {/* ── Quick action: open the BookMyShow-style availability grid ── */}
            <div className="flex justify-end">
              <button
  type="button"
  onClick={() => navigate("/lab-management/facility-booking")}
  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-br from-[#08757c] to-[#005b60] hover:opacity-90 active:scale-95 transition-all shadow-lg shadow-[#08757c]/20"
>
  <span className="material-symbols-outlined text-[18px]">grid_view</span>
  Available Labs
</button>
            </div>

            {/* ── STEP 1: Location ── */}
            <div>
              <SectionHeader
                step={1}
                icon="location_on"
                title="Select Location"
                isDone={!!selectedLocation}
                selectedLabel={selectedLocation?.cityName}
              />
              <LabBookingStep1Location
                locations={locations}
                selectedLocation={selectedLocation}
                onSelect={(loc) => {
                  setSelectedLocation(loc);
                  setErrors((prev) => ({ ...prev, location: "" }));
                  setTimeout(() => {
                    labsSectionRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }, 0);
                }}
                error={errors.location}
              />
            </div>

            {/* ── STEP 2: Lab — shown after location selected ── */}
            {selectedLocation && (
              <div ref={labsSectionRef}>
                <SectionHeader
                  step={2}
                  icon="biotech"
                  title={`Available Labs — ${selectedLocation.cityName}`}
                  isDone={!!selectedLab}
                  selectedLabel={selectedLab?.facilityName}
                />
                <LabBookingStep2Lab
                  labs={labs}
                  selectedLab={selectedLab}
                  selectedLocationName={selectedLocation.cityName}
                  onSelect={(lab) => {
                    setSelectedLab(lab);
                    setErrors((prev) => ({ ...prev, lab: "" }));
                    setTimeout(() => {
                      scheduleSectionRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }, 0);
                  }}
                  error={errors.lab}
                />
              </div>
            )}

            {/* ── STEP 3: Schedule — shown after lab selected ── */}
            {selectedLab && (
              <div ref={scheduleSectionRef}>
                <SectionHeader
                  step={3}
                  icon="edit_note"
                  title="Booking Details"
                  isDone={false}
                />
                <LabBookingStep3Schedule
                  form={form}
                  errors={errors}
                  onChange={handleFormChange}
                  onConfirm={handleConfirm}
                  onScheduleChange={(s) => setScheduleSummary(s)}
                  isSaving={isSaving}
                  saveError={saveError}
                  selectedLab={selectedLab}
                />
              </div>
            )}
          </div>

          {/* ── RIGHT: Booking Summary ── */}
          <div className="col-span-12 lg:col-span-4" ref={rightColRef}>
            <div
              className="space-y-4"
              style={
                pinnedRect
                  ? {
                      position: "fixed",
                      top: 96,
                      left: pinnedRect.left,
                      width: pinnedRect.width,
                      zIndex: 30,
                      maxHeight: "calc(100vh - 112px)",
                      overflowY: "auto",
                    }
                  : undefined
              }
            >
              <BookingSummary
                selectedLocation={selectedLocation}
                selectedLab={selectedLab}
                form={{ ...form, ...scheduleSummary }}
              />
              <GanttSideCard companyID={companyID} />
            </div>
          </div>
        </div>
      </div>

      {showLabGrid &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 99997,
              background: "rgba(15,23,23,0.55)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 20,
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 20,
                width: "100%",
                maxWidth: 1600,
                maxHeight: "calc(100vh - 40px)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
              }}
            >
              <FacilityBookingScheduler onClose={() => setShowLabGrid(false)} />
            </div>
          </div>,
          document.body,
        )}

      {showAvailability && (
        <LabAvailabilityPopup
          locationName={selectedLocation?.cityName}
          initialFacilityId={prefillFacilityId}
          onClose={() => {
            // Closing the booking popup goes back to the "All Labs" grid it
            // was opened from, not straight out to the page — matches
            // "back" rather than "exit the whole flow".
            setShowAvailability(false);
            setPrefillFacilityId(null);
            setShowLabGrid(true);
          }}
        />
      )}
    </DashboardLayout>
  );
}
